import { v4 as uuidv4 } from 'uuid';
import { marked } from 'marked';
import {
	GenerateTask,
	GraphMode,
	BrandPalette,
	IdeateGroundingSnapshot,
	Motivations,
	NCCluster,
	NCNode,
	PositioningAnalysisSource,
	PositioningGenerationContext,
	PositioningSourceContext,
	UserPersona,
} from '../../../../src/commonTypes';
import {
	getHeartIcon,
	closeAiContainers,
	showCommonIdeasNodesPrompt,
	toggleToolContainer,
	updateSelectedCommonIdeasNodes,
} from './toolsUI';
import * as ncApi from '../apiRequests';
import {
	finishLoading,
	getGraphModeFromHash,
	removeChildren,
	showAlert,
	startLoading,
} from './uiUtils';
import { IdeateGraph, InspireGraph } from '../graphs';
import { ANALYSES_TYPES, AnalysisKey } from '../../../../src/commonConstants';
import {
	POSITIONING_GENERATE_TASKS,
	generateResultSaveDestination,
	isGenerateTaskVisible,
} from './generateTaskVisibility';
import {
	analysesForPositioningGenerate,
	ensurePositioningClusterCenter,
	hasPositioningSourceContext,
	missingIdeateSourceMessage,
} from '../../../../src/services/board/ideatePositioning';
import {
	assignSocialPerspectiveBlueprint,
	isSocialPosterProfileEvidence,
} from '../../../../src/services/board/ideateGroundingEvidence';
import * as tutorialEvents from '../tutorials/tutorialEvents';
import {
	getBoardImages,
	like,
	motivationsStakeholderImage,
	userPersonaImage,
	uploadBoardImage,
} from '../apiRequests';
import { levenshteinDistance, userPersonaToString } from '../../../../src/commonMisc';
import tippy, { followCursor, hideAll, roundArrow } from 'tippy.js';
import { clearFlowyHighlight, highlightFlowyTarget } from './flowyHighlight';
import {
	addGenerateResultItemToBoard,
	likeGenerateResultItem,
	pickGenerateResultItem,
} from './flowyGenerateActions';

const IDEATE_CLUSTER_TASKS: GenerateTask[] = ['persona', 'character', 'motivations', 'audience'];

const generateTooltipIntro: Record<GraphMode, string> = {
	inspire: 'On Inspire, Generate expands the selected node into creative directions and media.',
	ideate:
		'On Ideate, Generate turns the selected psychographic analyses and board or PDF context into positioning outputs.',
	moodboard: 'On Synthesise, Generate creates visual material from the selected moodboard item.',
};

const formatToolList = (tools: string[]): string => {
	if (tools.length < 2) return tools[0] || 'None';
	return `${tools.slice(0, -1).join(', ')} and ${tools.at(-1)}`;
};

const updateGenerateTooltip = (container: HTMLElement, graphMode: GraphMode) => {
	const tools = Array.from(
		container.querySelectorAll<HTMLButtonElement>('.select-option button:not(.hidden)')
	)
		.map((button) => button.innerText.trim())
		.filter((label, index, labels) => Boolean(label) && labels.indexOf(label) === index);
	const infoButton = container.querySelector<HTMLElement>('.breadcrumbs .info');
	if (infoButton) {
		infoButton.dataset.tooltip = `${generateTooltipIntro[graphMode]}<br><br><strong>Available tools:</strong> ${formatToolList(tools)}.`;
	}
};

type GenerateResultEntry = {
	id?: string;
	snippet: string;
	title: string;
	positioningContext?: PositioningGenerationContext;
};

type BackendToolResultEntry = {
	id?: string;
	title?: string;
	snippet?: string;
	label?: string;
	url?: string;
	source?: string;
	imageUrl?: string;
	videoUrl?: string;
};

function getIdeateTaskString(task: GenerateTask, nodes: Array<NCNode>, inputString: string) {
	if (IDEATE_CLUSTER_TASKS.includes(task)) {
		const node = nodes[0];
		if (node.parent) {
			// This node is part of a cluster so either the whole cluster or just this node depending on user selection
			const clusterCenterNode = node.parent;
			const ideateOptions = document.querySelector('.ideate-options')!;
			ideateOptions.classList.remove('hidden');
			const ideateOptionsCheckbox = ideateOptions.querySelector('input')!;
			ideateOptionsCheckbox.onchange = () => {
				fetchGenerateResults(nodes, task);
			};
			if (ideateOptionsCheckbox.checked) {
				// Include whole cluster
				const clusterNodes = globalThis.neuroCreate.graph!.nodes.filter(
					(n) => n.parent && n.parent.uid === clusterCenterNode.uid
				);
				return `${node.label || ''} ${clusterNodes.map((n) => n.label).join(' ')}`;
			} else {
				// Include cluster center node along with current node
				return `${node.label || ''} ${clusterCenterNode.label}`;
			}
		} else {
			// This node is the center of a cluster, so include whole cluster
			const clusterNodes = globalThis.neuroCreate.graph!.nodes.filter(
				(n) => n.parent && n.parent.uid === node.uid
			);
			return `${node.label || ''} ${clusterNodes.map((n) => n.label).join(' ')}`;
		}
	}
	// Not ideate task, so process input normally
	return inputString;
}

export const createStakeholderDiv = (stakeholder: Motivations['stakeholders'][0]) => {
	const stakeholderDiv = document.createElement('div');
	stakeholderDiv.className = 'stakeholder';
	const name = document.createElement('p');
	name.className = 'name';
	name.innerText = stakeholder.name;
	stakeholderDiv.append(name);
	const description = document.createElement('p');
	description.className = 'description';
	description.innerText = stakeholder.description;
	stakeholderDiv.append(description);
	if (stakeholder.imageUrl) {
		const img = document.createElement('img');
		img.src = stakeholder.imageUrl;
		img.width = 140;
		stakeholderDiv.prepend(img);
	}
	return stakeholderDiv;
};

const createResultCard = (
	modifier: 'persona' | 'character' | 'motivation',
	title: string,
	summary: string,
	sections: Array<{ title: string; value: string }>,
	mediaOrAction?: HTMLElement,
	primaryAction?: HTMLElement
) => {
	const details = document.createElement('details');
	details.className = `generate-result-card generate-result-card--${modifier}`;
	details.open = false;

	const cardSummary = document.createElement('summary');
	cardSummary.className = 'generate-result-card__summary';
	cardSummary.addEventListener('click', (event) => event.stopPropagation());

	const header = document.createElement('div');
	header.className = 'generate-result-card__header';

	const eyebrow = document.createElement('div');
	eyebrow.className = 'generate-result-card__eyebrow';
	eyebrow.innerText =
		modifier === 'persona'
			? 'Persona option'
			: modifier === 'character'
				? 'Character option'
				: 'Stakeholder motivation';
	header.append(eyebrow);

	const titleElement = document.createElement('h4');
	titleElement.className = 'generate-result-card__title';
	titleElement.innerText = title;
	header.append(titleElement);

	cardSummary.append(header);

	const summaryText = document.createElement('p');
	summaryText.className = 'generate-result-card__preview';
	summaryText.innerText = summary;
	cardSummary.append(summaryText);
	details.append(cardSummary);

	const body = document.createElement('div');
	body.className = 'generate-result-card__body';
	body.addEventListener('click', (event) => event.stopPropagation());

	if (mediaOrAction) {
		const media = document.createElement('div');
		media.className = 'generate-result-card__media';
		media.append(mediaOrAction);
		body.append(media);
	}

	for (const section of sections) {
		if (!section.value) continue;
		const sectionElement = document.createElement('section');
		sectionElement.className = 'generate-result-card__section';

		const sectionTitle = document.createElement('h5');
		sectionTitle.className = 'generate-result-card__section-title';
		sectionTitle.innerText = section.title;
		sectionElement.append(sectionTitle);

		const sectionText = document.createElement('p');
		sectionText.innerText = section.value;
		sectionElement.append(sectionText);

		body.append(sectionElement);
	}

	if (primaryAction) {
		const actions = document.createElement('div');
		actions.className = 'generate-result-card__actions';
		actions.append(primaryAction);
		body.append(actions);
	}

	details.append(body);
	return details;
};

const firstSentence = (text: string) => {
	const trimmed = text.trim();
	if (!trimmed) return '';
	const sentence = trimmed.match(/^(.+?[.!?])(\s|$)/);
	return sentence ? sentence[1] : trimmed;
};

const extractMotivationLine = (description: string) => {
	const motivationLine = description
		.split('\n')
		.map((line) => line.trim())
		.find((line) => /^motivation:/i.test(line));
	return motivationLine?.replace(/^motivation:\s*/i, '').trim() || firstSentence(description);
};

const createPersonaCard = (
	persona: UserPersona,
	mediaOrAction?: HTMLElement,
	primaryAction?: HTMLElement,
	modifier: 'persona' | 'character' = 'persona',
	positioningContext?: PositioningGenerationContext
) => {
	const card = createResultCard(
		modifier,
		persona.name,
		`${persona.ageGroup || '25–34'} · ${firstSentence(persona.biography) || persona.needs}`,
		[
			{ title: 'Age group', value: persona.ageGroup || '25–34' },
			...(persona.gender ? [{ title: 'Gender', value: persona.gender }] : []),
			{ title: 'Who they are', value: persona.biography },
			{ title: 'Goals', value: persona.goals },
			{ title: 'Needs', value: persona.needs },
			{ title: 'Pain points', value: persona.painPoints },
			{ title: 'Motivations', value: persona.motivations },
			{ title: 'Influences', value: persona.influences },
		],
		mediaOrAction,
		primaryAction
	);

	// The collapsed card is the comparison surface: surface the decision-relevant
	// need and barrier before the richer biography and psychographic foundations
	// that remain available after expansion.
	if (modifier === 'persona') {
		const summary = card.querySelector<HTMLElement>('.generate-result-card__summary');
		if (summary) {
			const signals = document.createElement('div');
			signals.className = 'generate-result-card__persona-signals';
			[
				{ label: 'Need', value: firstSentence(persona.needs || persona.goals) },
				{ label: 'Barrier', value: firstSentence(persona.painPoints) },
			].forEach(({ label, value }) => {
				if (!value) return;
				const signal = document.createElement('p');
				signal.className = 'generate-result-card__persona-signal';
				const signalLabel = document.createElement('strong');
				signalLabel.innerText = `${label}: `;
				signal.append(signalLabel, value);
				signals.append(signal);
			});

			const socialLane = positioningContext?.socialPerspectiveBlueprint;
			if (socialLane) {
				const pills = document.createElement('div');
				pills.className = 'generate-result-card__persona-pills';
				[socialLane.primaryStance, socialLane.sentiment, ...socialLane.emotions.slice(0, 1)]
					.filter((value): value is string => Boolean(value))
					.forEach((value) => {
						const pill = document.createElement('span');
						pill.className = `generate-result-card__persona-pill generate-result-card__persona-pill--${value.toLowerCase()}`;
						pill.innerText = value;
						pills.append(pill);
					});
				signals.append(pills);
			}

			if (signals.childElementCount) summary.append(signals);
		}
	}

	return card;
};

const createBrandPaletteCard = (
	palette: BrandPalette,
	onChange: (palette: BrandPalette) => void
) => {
	const wrapper = document.createElement('article');
	wrapper.className = 'generate-palette-card';
	const heading = document.createElement('h4');
	heading.innerText = palette.name;
	const rationale = document.createElement('p');
	rationale.innerText = palette.rationale;
	const colours = document.createElement('div');
	colours.className = 'generate-palette-card__colours';
	const renderColours = () => {
		colours.replaceChildren();
		colours.style.gridTemplateColumns = `repeat(${palette.colours.length}, minmax(0, 1fr))`;
		palette.colours.forEach((colour, index) => {
			const item = document.createElement('div');
			item.className = 'generate-palette-card__colour';
			const swatch = document.createElement('input');
			swatch.type = 'color';
			swatch.className = 'generate-palette-card__swatch';
			swatch.value = /^#[0-9a-f]{6}$/i.test(colour.hex) ? colour.hex : '#000000';
			swatch.title = `Edit ${colour.name}`;
			swatch.setAttribute('aria-label', `Edit ${colour.name} colour`);
			swatch.addEventListener('input', () => {
				colour.hex = swatch.value.toUpperCase();
				label.innerText = `${colour.name}\n${colour.hex}\n${colour.role}`;
				onChange(palette);
			});
			const remove = document.createElement('button');
			remove.type = 'button';
			remove.className = 'generate-palette-card__remove';
			remove.innerText = '×';
			remove.title = `Remove ${colour.name}`;
			remove.setAttribute('aria-label', `Remove ${colour.name} from palette`);
			remove.hidden = palette.colours.length <= 1;
			remove.addEventListener('click', (event) => {
				event.preventDefault();
				event.stopPropagation();
				palette.colours.splice(index, 1);
				onChange(palette);
				renderColours();
			});
			const label = document.createElement('p');
			label.innerText = `${colour.name}\n${colour.hex}\n${colour.role}`;
			item.append(swatch, remove, label);
			colours.append(item);
		});
	};
	renderColours();
	wrapper.append(heading, rationale, colours);
	return wrapper;
};

const createMotivationCard = (
	motivations: Motivations,
	stakeholder: Motivations['stakeholders'][0],
	mediaOrAction?: HTMLElement,
	primaryAction?: HTMLElement
) =>
	createResultCard(
		'motivation',
		`${stakeholder.name}: ${extractMotivationLine(stakeholder.description)}`,
		firstSentence(stakeholder.description) || motivations.intro,
		[
			{ title: 'Motivation', value: stakeholder.description },
			{ title: 'Context', value: motivations.intro },
			{ title: 'Design implication', value: motivations.outro },
		],
		mediaOrAction,
		primaryAction
	);

const attachSaveButtons = (
	itemContent: HTMLElement,
	heartIcon: HTMLElement,
	destination: 'idea-box' | 'positioning-map'
) => {
	const cards = itemContent.querySelectorAll<HTMLElement>('.generate-result-card');
	const targets = cards.length ? Array.from(cards) : [itemContent];
	targets.forEach((card) => {
		const body = card.querySelector<HTMLElement>('.generate-result-card__body') || card;
		let actions = body.querySelector<HTMLElement>('.generate-result-card__actions');
		if (!actions) {
			actions = document.createElement('div');
			actions.className = 'generate-result-card__actions';
			body.append(actions);
		}

		const button = document.createElement('button');
		button.type = 'button';
		button.className = 'generate-result-card__button';
		if (destination === 'positioning-map') button.classList.add('positioning-save-output');
		const syncButtonState = () => {
			const isLiked = heartIcon.classList.contains('liked');
			const destinationLabel = destination === 'positioning-map' ? 'positioning map' : 'Idea Box';
			button.innerText = isLiked ? `Added to ${destinationLabel}` : `Add to ${destinationLabel}`;
			button.disabled = isLiked;
			button.setAttribute('aria-pressed', String(isLiked));
		};
		button.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();
			if (!heartIcon.classList.contains('liked')) {
				heartIcon.click();
			}
			syncButtonState();
		});
		new MutationObserver(syncButtonState).observe(heartIcon, {
			attributes: true,
			attributeFilter: ['class'],
		});
		syncButtonState();
		actions.append(button);
	});
};

const splitMotivationEntries = (entries: Array<GenerateResultEntry>): Array<GenerateResultEntry> =>
	entries.flatMap((entry) => {
		try {
			const motivations = JSON.parse(entry.snippet) as Motivations;
			if (!motivations.stakeholders || motivations.stakeholders.length <= 1) {
				return [entry];
			}
			return motivations.stakeholders.map((stakeholder, index) => {
				const singleMotivation: Motivations = {
					...motivations,
					stakeholders: [stakeholder],
				};
				return {
					id: entry.id ? `${entry.id}:${stakeholder.name || index}` : undefined,
					snippet: JSON.stringify(singleMotivation),
					title: stakeholder.name || entry.title,
					positioningContext: entry.positioningContext,
				};
			});
		} catch {
			return [entry];
		}
	});

const POSITIONING_RESULTS_DISMISSED_AFTER_SAVE = new Set<GenerateTask>([
	'persona',
	'motivations',
	'story',
	'brand-colours',
	'brand-tone',
	'character',
]);
const POSITIONING_GENERATION_CLIENT_DEADLINE_MS = 90_000;

type GenerateProgressStage =
	| 'analyses'
	| 'searching'
	| 'sources'
	| 'social'
	| 'grounding'
	| 'generating';
type GenerateProgressOwner = 'positioning' | 'inspire' | 'agent';

const progressStageOrder = (owner: GenerateProgressOwner): GenerateProgressStage[] =>
	owner === 'positioning'
		? ['analyses', 'sources', 'social', 'grounding', 'generating']
		: ['searching', 'sources', 'social', 'grounding', 'generating'];

const configureGenerateProgress = ({
	owner,
	title,
	includeSocial = false,
}: {
	owner: GenerateProgressOwner;
	title: string;
	includeSocial?: boolean;
}): HTMLElement | null => {
	const progress = document.querySelector<HTMLElement>(
		'#generate-container .positioning-generation-progress'
	);
	if (!progress) return null;
	progress.dataset.progressOwner = owner;
	progress.dataset.progressStage = '';
	progress.dataset.includeSocial = includeSocial ? 'true' : 'false';
	progress.classList.remove('hidden');
	const titleElement = progress.querySelector<HTMLElement>(
		'.positioning-generation-progress__title'
	);
	if (titleElement) titleElement.innerText = title;
	const primaryStage = progress.querySelector<HTMLElement>('[data-generation-primary-stage]');
	if (primaryStage) {
		primaryStage.dataset.generationStage = owner === 'positioning' ? 'analyses' : 'searching';
		primaryStage.innerText =
			owner === 'positioning' ? 'Running selected analyses' : 'Searching for relevant context';
	}
	progress.querySelectorAll<HTMLElement>('[data-generation-stage]').forEach((element) => {
		element.classList.remove('is-active', 'is-complete');
		if (element.dataset.generationStage === 'social') {
			element.hidden = !includeSocial;
		}
	});
	const detail = progress.querySelector<HTMLElement>('.positioning-generation-progress__detail');
	if (detail) detail.innerText = '';
	progress.querySelector('.positioning-generation-recovery')?.classList.add('hidden');
	return progress;
};

const updateGenerateProgress = (stage: GenerateProgressStage, detail: string): void => {
	const progress = document.querySelector<HTMLElement>(
		'#generate-container .positioning-generation-progress'
	);
	if (!progress) return;
	progress.classList.remove('hidden');
	const owner = (progress.dataset.progressOwner || 'inspire') as GenerateProgressOwner;
	const includeSocial = progress.dataset.includeSocial === 'true';
	const stages = progressStageOrder(owner).filter((entry) => includeSocial || entry !== 'social');
	const activeIndex = stages.indexOf(stage);
	if (activeIndex < 0) return;
	const currentIndex = stages.indexOf(
		(progress.dataset.progressStage || '') as GenerateProgressStage
	);
	if (currentIndex >= 0 && activeIndex < currentIndex) return;
	progress.dataset.progressStage = stage;
	progress.querySelectorAll<HTMLElement>('[data-generation-stage]').forEach((element) => {
		const index = stages.indexOf(element.dataset.generationStage as GenerateProgressStage);
		element.classList.toggle('is-active', index === activeIndex);
		element.classList.toggle('is-complete', index >= 0 && index < activeIndex);
	});
	const detailElement = progress.querySelector<HTMLElement>(
		'.positioning-generation-progress__detail'
	);
	if (detailElement) detailElement.innerText = detail;
};

const showPositioningGenerationRecovery = ({
	onRetry,
	onContinueWithoutSources,
}: {
	onRetry: () => void;
	onContinueWithoutSources: () => void;
}): void => {
	const progress = document.querySelector<HTMLElement>(
		'#generate-container .positioning-generation-progress'
	);
	const recovery = progress?.querySelector<HTMLElement>('.positioning-generation-recovery');
	if (!progress || !recovery) return;
	progress.classList.remove('hidden');
	recovery.classList.remove('hidden');
	const retry = recovery.querySelector<HTMLButtonElement>('[data-generation-retry]');
	const continueWithoutSources = recovery.querySelector<HTMLButtonElement>(
		'[data-generation-without-sources]'
	);
	if (retry) retry.onclick = onRetry;
	if (continueWithoutSources) continueWithoutSources.onclick = onContinueWithoutSources;
};

export const hideGenerateProgress = (owner?: GenerateProgressOwner): void => {
	const progress = document.querySelector<HTMLElement>(
		'#generate-container .positioning-generation-progress'
	);
	if (!progress || (owner && progress.dataset.progressOwner !== owner)) return;
	progress.classList.add('hidden');
};

export const startBackendGenerateProgress = (task?: string | null): void => {
	const taskLabel = (task || 'results').replace(/-/g, ' ');
	configureGenerateProgress({
		owner: 'agent',
		title: `Generating ${taskLabel}`,
		includeSocial: task === 'persona' || task === 'motivations',
	});
	updateGenerateProgress('searching', 'Preparing the generation workflow…');
};

export const updateBackendGenerateProgress = (message: string): void => {
	const progress = document.querySelector<HTMLElement>(
		'#generate-container .positioning-generation-progress'
	);
	if (
		!progress ||
		progress.dataset.progressOwner !== 'agent' ||
		progress.classList.contains('hidden')
	) {
		return;
	}
	const normalized = message.toLowerCase();
	const currentStage = (progress.dataset.progressStage || 'searching') as GenerateProgressStage;
	const stage = /social listening|audience voices/.test(normalized)
		? 'social'
		: /generat|format|evaluat|adding/.test(normalized)
			? 'generating'
			: /ground/.test(normalized)
				? 'grounding'
				: /source|search|find/.test(normalized)
					? 'sources'
					: currentStage;
	updateGenerateProgress(stage, message);
};

const showGenerateError = (message?: string) => {
	const container = document.querySelector<HTMLElement>('#generate-container')!;
	const loadingIndicator = container.querySelector('.loading')!;
	loadingIndicator.classList.add('hidden');
	hideGenerateProgress();
	showAlert(
		message ||
			'Unable to generate results. Please try again later or contact us if this problem continues.'
	);
};

const currentIdeateMissingSourceMessage = (): string =>
	missingIdeateSourceMessage(
		Boolean((globalThis.neuroCreate.graph as IdeateGraph | undefined)?.isCollaborative)
	);

const renderSafeMarkdown = (markdown: string): DocumentFragment => {
	const template = document.createElement('template');
	template.innerHTML = marked.parse(markdown, { async: false }) as string;
	const allowedTags = new Set([
		'A',
		'BLOCKQUOTE',
		'BR',
		'CODE',
		'EM',
		'H1',
		'H2',
		'H3',
		'H4',
		'H5',
		'H6',
		'LI',
		'OL',
		'P',
		'PRE',
		'STRONG',
		'UL',
	]);
	const blockedTags = new Set(['IFRAME', 'OBJECT', 'SCRIPT', 'STYLE']);
	template.content.querySelectorAll('*').forEach((element) => {
		if (blockedTags.has(element.tagName)) {
			element.remove();
			return;
		}
		if (!allowedTags.has(element.tagName)) {
			element.replaceWith(...Array.from(element.childNodes));
			return;
		}
		const href = element.tagName === 'A' ? element.getAttribute('href') : null;
		Array.from(element.attributes).forEach((attribute) => element.removeAttribute(attribute.name));
		if (element instanceof HTMLAnchorElement && href) {
			try {
				const url = new URL(href, window.location.origin);
				if (['http:', 'https:', 'mailto:'].includes(url.protocol)) {
					element.href = url.href;
					element.target = '_blank';
					element.rel = 'noopener noreferrer';
				}
			} catch {
				// Invalid links are kept as plain anchor text.
			}
		}
	});
	return template.content;
};

const createPositioningBasis = (context: PositioningGenerationContext) => {
	const basis = document.createElement('section');
	basis.className = 'positioning-result-basis';
	const sources = document.createElement('div');
	sources.className = 'positioning-result-basis__sources';
	context.sources.forEach((source) => {
		const sourceElement = document.createElement('p');
		sourceElement.title = source.cluster;
		const analysisName = document.createElement('strong');
		analysisName.innerText = source.analysisName;
		const selectedNodes = document.createElement('span');
		selectedNodes.innerText = source.nodes.join(', ') || source.cluster;
		sourceElement.append(analysisName, document.createTextNode(' \u2192 '), selectedNodes);
		sources.append(sourceElement);
	});
	basis.append(sources);
	const groundingEvidence = context.groundingEvidence || [];
	const snapshot = context.groundingSnapshot;
	const boardCount = (snapshot?.selectedBoardConcepts ?? snapshot?.boardConcepts ?? []).length;
	const likedCount = (snapshot?.selectedLikedResearch ?? snapshot?.likedResearch ?? []).length;
	// Foundations now have their own analysis explorer; keeping this result row
	// compact avoids repeating a long, non-interactive evidence dump per output.
	if (false && (groundingEvidence.length || boardCount || likedCount)) {
		const grounding = document.createElement('div');
		grounding.className = 'positioning-result-basis__grounding';
		const heading = document.createElement('strong');
		heading.innerText = 'Grounding';
		grounding.append(heading);
		if (boardCount || likedCount) {
			const boardRow = document.createElement('p');
			boardRow.innerText = [
				boardCount ? `${boardCount} board concept${boardCount === 1 ? '' : 's'}` : '',
				likedCount ? `${likedCount} Idea Box signal${likedCount === 1 ? '' : 's'}` : '',
			]
				.filter(Boolean)
				.join(' · ');
			grounding.append(boardRow);
		}
		const compactCount = (value: number) =>
			new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
		groundingEvidence.slice(0, 6).forEach((item) => {
			const row = document.createElement('p');
			const isSocialPost = item.source.startsWith('social-profile:');
			if (isSocialPost && item.image) {
				const media = document.createElement('span');
				media.className = 'positioning-result-basis__social-media';
				const image = document.createElement('img');
				image.src = item.image;
				image.alt = item.isVideo ? 'Video preview' : 'Social post image';
				image.loading = 'lazy';
				image.referrerPolicy = 'no-referrer';
				image.addEventListener('error', () => media.remove());
				media.append(image);
				if (item.isVideo) {
					const video = document.createElement('i');
					video.textContent = 'Video';
					media.append(video);
				}
				row.append(media);
			}
			const source = document.createElement('strong');
			source.innerText =
				item.source === 'social' || item.source.startsWith('social')
					? 'Social'
					: item.source.charAt(0).toUpperCase() + item.source.slice(1);
			row.append(source, document.createTextNode(` · ${item.title}`));
			if (isSocialPost && typeof item.engagement?.likes === 'number') {
				const likes = document.createElement('small');
				likes.className = 'positioning-result-basis__likes';
				likes.textContent = `${compactCount(item.engagement.likes)} likes`;
				row.append(likes);
			}
			if (item.detail && item.detail !== item.title) {
				row.title = item.detail;
			}
			grounding.append(row);
		});
		if (groundingEvidence.length > 6) {
			const more = document.createElement('p');
			more.innerText = `+${groundingEvidence.length - 6} more live`;
			grounding.append(more);
		}
		basis.append(grounding);
	}
	return basis;
};

export const showGenerateResults = (
	generateTask: GenerateTask,
	entries: Array<GenerateResultEntry>,
	isPreviousResults = false,
	nodes?: Array<NCNode>,
	options: {
		suppressTutorialEvent?: boolean;
		recommendedResultId?: string | null;
		recommendedResultIds?: string[];
	} = {}
): GenerateResultEntry[] => {
	const container = document.querySelector<HTMLElement>('#generate-container')!;
	const loadingIndicator = container.querySelector('.loading')!;
	const resultsList = container.querySelector('.results-list')!;
	const renderedEntries: GenerateResultEntry[] = [];

	loadingIndicator.classList.add('hidden');
	hideGenerateProgress();
	if (entries.length > 0 && container.dataset.positioningMode === 'true') {
		const positioningConfig = container.querySelector<HTMLDetailsElement>(
			'.positioning-generate-context'
		);
		if (positioningConfig) positioningConfig.open = false;
		container.querySelector('.positioning-generate-more')?.classList.remove('hidden');
	}

	if (entries.length === 0 && !isPreviousResults) {
		resultsList.innerHTML = '<p><strong>No results</strong></p>';
	} else {
		if (isPreviousResults) {
			const small = document.createElement('small');
			small.innerText = `Earlier generated ${generateTask.startsWith('image') ? 'images' : 'videos'}: `;
			resultsList.append(small);
			console.log(entries);
		}
		const resultEntries =
			generateTask === 'motivations' ? splitMotivationEntries(entries) : entries;
		const recommendedIds = new Set([
			...(options.recommendedResultId ? [options.recommendedResultId] : []),
			...(options.recommendedResultIds || []),
		]);
		for (const [index, entry] of resultEntries.entries()) {
			const clientId = entry.id || `generate-${generateTask}-${uuidv4()}`;
			entry.id = clientId;
			const saveDestination = generateResultSaveDestination({
				generateTask,
				graphMode: globalThis.neuroCreate.graph?.graphMode || getGraphModeFromHash()[0],
				positioningMode: container.dataset.positioningMode === 'true',
			});
			const isIdeaBoxOnlyResult = saveDestination === 'idea-box';
			const isPositioningResult = saveDestination === 'positioning-map';
			let getTextForIdeabox = (): string | null => {
				if (generateTask === 'audience') {
					return itemContent.innerText;
				} else if (generateTask.startsWith('image')) {
					return null;
				} else if (generateTask.startsWith('video')) {
					return null;
				} else {
					return entry.snippet;
				}
			};

			const listItem = document.createElement('li');
			if (entry.id) {
				listItem.dataset.resultId = entry.id;
			}
			listItem.dataset.resultIndex = String(index);
			listItem.dataset.resultTitle = entry.title;
			listItem.dataset.resultSnippet = entry.snippet;
			if (entry.id && recommendedIds.has(entry.id)) {
				listItem.classList.add('flowy-recommended-result');
			}
			const itemContent = document.createElement('div');
			itemContent.className = 'text';
			if (generateTask === 'audience') {
				itemContent.innerHTML = entry.snippet;
			} else if (generateTask.startsWith('image')) {
				itemContent.innerHTML = `<img src=${entry.snippet} alt='' width=200 />`;
			} else if (generateTask.startsWith('video')) {
				itemContent.innerHTML = `
				<video width="250" controls>
				  <source src="${entry.snippet}" type="video/mp4">
				  Your browser does not support the video tag.
				</video>
			  `;
			} else if (generateTask === 'persona' || generateTask === 'character') {
				const persona = JSON.parse(entry.snippet) as UserPersona;
				getTextForIdeabox = () => JSON.stringify(persona);
				itemContent.dataset.boardText = userPersonaToString(persona);
				let mediaOrAction: HTMLElement | undefined;
				if (persona.imageUrl) {
					const personaImg = document.createElement('img');
					personaImg.src = persona.imageUrl;
					personaImg.width = 200;
					mediaOrAction = personaImg;
				} else {
					const generateImageButton = document.createElement('image-generator-with-quota');
					generateImageButton.addEventListener('click', (event) => event.stopPropagation());
					const generatePersonaImage = () => {
						const boardId = document.body.dataset.boardId!;
						userPersonaImage(boardId, persona)
							.then(async (imageResult) => {
								const img = document.createElement('img');
								img.src = imageResult.snippet;
								img.width = 200;
								generateImageButton.remove();
								itemContent.querySelector('.generate-result-card__media')?.prepend(img);
								persona.imageUrl = imageResult.snippet;
								removeImageButton.hidden = false;
								uploadLabel.firstChild!.textContent = 'Replace image';
								const syncSavedPersonaImage = async (attempt = 0): Promise<void> => {
									if (!listItem.querySelector('.heart')?.classList.contains('liked')) return;
									const saved = await ncApi.updateSavedPersonaImage(
										boardId,
										clientId,
										imageResult.snippet
									);
									if (!saved && attempt < 2) {
										window.setTimeout(() => void syncSavedPersonaImage(attempt + 1), 300);
										return;
									}
									if (saved) {
										window.dispatchEvent(
											new CustomEvent('positioning-output-saved', {
												detail: { type: generateTask, clientId },
											})
										);
									}
								};
								try {
									await syncSavedPersonaImage();
								} catch (error) {
									console.error('Unable to sync generated persona image', error);
									showAlert(
										'The image was generated, but the saved persona could not be updated. Please try adding the persona again.'
									);
								}
							})
							.catch(() => {
								showAlert(
									'Unable to generate image. Please try again later or contact us if this problem continues.'
								);
							});
					};
					generateImageButton.onClick = generatePersonaImage;
					mediaOrAction = generateImageButton;
					if (
						generateTask === 'persona' &&
						document.body.dataset.autoGeneratePersonaImages === 'true'
					) {
						window.setTimeout(() => {
							if (generateImageButton.isConnected) generateImageButton.handleClick();
						}, 0);
					}
				}
				const imageControls = document.createElement('div');
				imageControls.className = 'generate-profile-image-controls';
				if (mediaOrAction) imageControls.append(mediaOrAction);
				const uploadLabel = document.createElement('label');
				uploadLabel.className = 'generate-profile-image-upload';
				uploadLabel.innerText = persona.imageUrl ? 'Replace image' : 'Upload image';
				const uploadInput = document.createElement('input');
				uploadInput.type = 'file';
				uploadInput.accept = 'image/png,image/jpeg,image/webp';
				uploadLabel.append(uploadInput);
				uploadInput.addEventListener(
					'change',
					() =>
						void (async () => {
							const file = uploadInput.files?.[0];
							if (!file) return;
							try {
								const imageUrl = await uploadBoardImage(document.body.dataset.boardId!, file);
								persona.imageUrl = imageUrl;
								removeImageButton.hidden = false;
								uploadLabel.firstChild!.textContent = 'Replace image';
								const existingImage = imageControls.querySelector('img');
								if (existingImage) existingImage.src = imageUrl;
								else {
									const image = document.createElement('img');
									image.src = imageUrl;
									image.width = 200;
									imageControls.prepend(image);
								}
								if (listItem.querySelector('.heart')?.classList.contains('liked')) {
									await ncApi.updateSavedPersonaImage(
										document.body.dataset.boardId!,
										clientId,
										imageUrl
									);
									window.dispatchEvent(new CustomEvent('positioning-output-saved'));
								}
							} catch (error) {
								console.error('Unable to upload profile image', error);
								showAlert('Unable to upload image. Please try again.');
							} finally {
								uploadInput.value = '';
							}
						})()
				);
				const removeImageButton = document.createElement('button');
				removeImageButton.type = 'button';
				removeImageButton.className = 'generate-profile-image-remove';
				removeImageButton.innerText = 'Remove image';
				removeImageButton.hidden = !persona.imageUrl;
				removeImageButton.addEventListener(
					'click',
					(event) =>
						void (async () => {
							event.preventDefault();
							event.stopPropagation();
							delete persona.imageUrl;
							imageControls.querySelector('img')?.remove();
							removeImageButton.hidden = true;
							uploadLabel.firstChild!.textContent = 'Upload image';
							try {
								if (listItem.querySelector('.heart')?.classList.contains('liked')) {
									await ncApi.updateSavedPersonaImage(document.body.dataset.boardId!, clientId, '');
									window.dispatchEvent(new CustomEvent('positioning-output-saved'));
								}
							} catch (error) {
								console.error('Unable to remove profile image', error);
								showAlert('Unable to remove image. Please try again.');
							}
						})()
				);
				imageControls.append(uploadLabel, removeImageButton);
				itemContent.append(
					createPersonaCard(
						persona,
						imageControls,
						undefined,
						generateTask === 'character' ? 'character' : 'persona',
						entry.positioningContext
					)
				);
			} else if (generateTask === 'motivations') {
				const motivations = JSON.parse(entry.snippet) as Motivations;
				getTextForIdeabox = () => JSON.stringify(motivations);
				itemContent.dataset.boardText = motivations.stakeholders
					.map((stakeholder) => `${stakeholder.name}: ${stakeholder.description}`)
					.join('\n\n');
				const title = entry.title;
				const stakeholderCards = new Map<Motivations['stakeholders'][0], HTMLElement>();

				const hasImages = motivations.stakeholders.some((stakeholder) => stakeholder.imageUrl);
				let generateImageButton: HTMLElement | undefined;
				if (!hasImages) {
					const imageButton = document.createElement('image-generator-with-quota');
					imageButton.align = 'right';
					imageButton.isQuotaOnRight = true;
					imageButton.cost = motivations.stakeholders.length;
					imageButton.addEventListener('click', (event) => event.stopPropagation());
					imageButton.onClick = () => {
						const boardId = document.body.dataset.boardId!;
						startLoading('Generating motivations images...');

						// Generate image for each stakeholder
						const promises = motivations.stakeholders.map((stakeholder, index) => {
							return motivationsStakeholderImage(
								boardId,
								stakeholder.name,
								stakeholder.description,
								title
							).then((imageResult) => {
								const img = document.createElement('img');
								img.src = imageResult.imageUrl;
								img.width = 140;
								stakeholderCards
									.get(motivations.stakeholders[index])
									?.querySelector('.generate-result-card__media')
									?.prepend(img);
								stakeholder.imageUrl = imageResult.imageUrl;
							});
						});

						Promise.all(promises)
							.then(() => {
								imageButton.remove();
								finishLoading();
								like(
									boardId,
									'motivations',
									JSON.stringify(motivations),
									null,
									false,
									title,
									undefined,
									undefined,
									entry.positioningContext
								);
							})
							.catch(() => {
								finishLoading();
								showAlert(
									'Unable to generate images. Please try again later or contact us if this problem continues.'
								);
							});
					};
					generateImageButton = imageButton;
					if (motivations.stakeholders.length > 1) {
						itemContent.prepend(generateImageButton);
					}
				}

				for (const stakeholder of motivations.stakeholders) {
					let mediaOrAction: HTMLElement | undefined;
					if (stakeholder.imageUrl) {
						const img = document.createElement('img');
						img.src = stakeholder.imageUrl;
						img.width = 140;
						mediaOrAction = img;
					} else if (generateImageButton && motivations.stakeholders.length === 1) {
						mediaOrAction = generateImageButton;
					}
					const card = createMotivationCard(motivations, stakeholder, mediaOrAction);
					stakeholderCards.set(stakeholder, card);
					itemContent.append(card);
				}
			} else if (generateTask === 'brand-colours') {
				const palette = JSON.parse(entry.snippet) as BrandPalette;
				itemContent.append(
					createBrandPaletteCard(palette, (updatedPalette) => {
						entry.snippet = JSON.stringify(updatedPalette);
					})
				);
			} else {
				itemContent.classList.add('generate-markdown');
				itemContent.append(renderSafeMarkdown(entry.snippet));
			}
			if (isPositioningResult && entry.positioningContext) {
				itemContent.append(createPositioningBasis(entry.positioningContext));
			}
			listItem.append(itemContent);
			const label = document.createElement('div');
			label.className = 'label';
			label.innerText = entry.title;
			if (!isPositioningResult) listItem.append(label);

			const heartIcon = getHeartIcon(
				generateTask,
				getTextForIdeabox,
				generateTask.startsWith('image') || generateTask.startsWith('video')
					? entry.snippet
					: undefined,
				entry.title,
				undefined,
				entry.positioningContext,
				clientId,
				(liked) => {
					if (
						liked &&
						isPositioningResult &&
						POSITIONING_RESULTS_DISMISSED_AFTER_SAVE.has(generateTask)
					) {
						listItem.remove();
					}
				}
			);
			if (saveDestination) {
				const saveLabel =
					saveDestination === 'positioning-map' ? 'Add to positioning map' : 'Add to Idea Box';
				heartIcon.title = saveLabel;
				heartIcon.setAttribute('aria-label', saveLabel);
				attachSaveButtons(itemContent, heartIcon, saveDestination);
			}
			listItem.append(heartIcon);

			if (isPreviousResults) {
				resultsList.append(listItem);
			} else {
				resultsList.prepend(listItem);
			}
			renderedEntries.push(entry);

			const graph = globalThis.neuroCreate.graph;
			if (graph) {
				const graphMode = graph.graphMode;
				if (graphMode === 'inspire') {
					listItem.classList.add('generate-list-item');
					if (generateTask.startsWith('image')) {
						listItem.classList.add('image-result');
					}

					if (nodes && nodes.length > 0 && !isIdeaBoxOnlyResult) {
						listItem.addEventListener('click', (e) => {
							const { nodeName, parentElement } = e.target as HTMLElement;
							const isHeartIconClicked =
								nodeName === 'IMG' && parentElement?.classList.contains('heart');
							if (!isHeartIconClicked) {
								if (generateTask.startsWith('image')) {
									// Add image to board
									createSingleNodeFromGenerate(nodes[0], '', entry.snippet, false, generateTask);
								} else if (generateTask.startsWith('video')) {
									// Add video to board
									createSingleNodeFromGenerate(nodes[0], '', entry.snippet, true, generateTask);
								} else {
									if (generateTask === 'haiku') {
										// replace line breaks with comma and whitespace
										itemContent.innerText = itemContent.innerText.replace(/\n/g, ', ');
									}
									createSingleNodeFromGenerate(
										nodes[0],
										itemContent.dataset.boardText || itemContent.innerText,
										undefined,
										false,
										generateTask
									);
								}
								listItem.remove();
								clearFlowyHighlight();
							}
						});
						const tooltip = tippy(listItem, {
							theme: 'neuro',
							arrow: roundArrow,
							delay: 0,
							duration: 0,
							offset: [0, 30],
							placement: 'bottom',
							followCursor: true,
							content: 'Add to board',
							plugins: [followCursor],
						});
						listItem?.addEventListener('mouseover', (e) => {
							const { nodeName, parentElement } = e.target as HTMLElement;
							const isHeartIconHovered =
								nodeName === 'IMG' && parentElement?.classList.contains('heart');
							if (!isHeartIconHovered) {
								tooltip.show();
							} else {
								hideAll();
							}
							return;
						});
					}
				}
			}
		}
	}

	if (!options.suppressTutorialEvent) {
		if (generateTask === 'haiku') {
			tutorialEvents.clickedHaikus();
		} else if (generateTask === 'persona') {
			tutorialEvents.clickedGenerateUserPersona();
		} else if (generateTask === 'motivations') {
			tutorialEvents.clickedGenerateMotivations();
		}
	}

	const resultIdsToHighlight = [
		...(options.recommendedResultId ? [options.recommendedResultId] : []),
		...(options.recommendedResultIds || []),
	];
	if (resultIdsToHighlight.length) {
		window.setTimeout(() => {
			highlightGenerateResult({
				resultIds: resultIdsToHighlight,
			});
		}, 80);
	}
	return renderedEntries;
};

const generateTaskFromBackendTask = (
	task?: string | null,
	tool?: string | null
): GenerateTask | null => {
	const raw = (task || tool || '').trim().toLowerCase();
	const aliases: Record<string, GenerateTask> = {
		brainstorm: 'brainstorm',
		brainstormsequence: 'brainstorm',
		common: 'common',
		commonideas: 'common',
		commonideassequence: 'common',
		persona: 'persona',
		personas: 'persona',
		character: 'character',
		characters: 'character',
		story: 'story',
		stories: 'story',
		'brand-colours': 'brand-colours',
		brandcolours: 'brand-colours',
		'brand-tone': 'brand-tone',
		brandtone: 'brand-tone',
		'tone-of-voice': 'brand-tone',
		toneofvoice: 'brand-tone',
		themespersonassequence: 'persona',
		personalitypersonassequence: 'persona',
		motivation: 'motivations',
		motivations: 'motivations',
		themesmotivationssequence: 'motivations',
		tagline: 'proverb',
		taglines: 'proverb',
		proverb: 'proverb',
		taglinessequence: 'proverb',
		haiku: 'haiku',
		haikus: 'haiku',
		haikussequence: 'haiku',
		'ai-haiku': 'ai-haiku',
		aihaiku: 'ai-haiku',
		aihaikus: 'ai-haiku',
		aihaikussequence: 'ai-haiku',
		image: 'image-inspire',
		imageinspire: 'image-inspire',
		'image-inspire': 'image-inspire',
		video: 'video-inspire',
		videoinspire: 'video-inspire',
		'video-inspire': 'video-inspire',
	};
	return aliases[raw] || null;
};

const normalizeBackendResultEntries = (
	task: GenerateTask,
	results: BackendToolResultEntry[]
): GenerateResultEntry[] =>
	results.map((result, index) => {
		const mediaUrl = task.startsWith('image')
			? result.imageUrl || result.url
			: task.startsWith('video')
				? result.videoUrl || result.url
				: '';
		const snippet = mediaUrl || result.snippet || result.label || result.title || '';
		return {
			id: result.id || result.url || result.title || result.label || `${task}-${index}`,
			title: result.title || result.label || result.source || `Result ${index + 1}`,
			snippet,
		};
	});

const resolveBackendSelectedNodes = ({
	selectedNodeIds,
	selectedNodeLabels,
}: {
	selectedNodeIds?: string[];
	selectedNodeLabels?: string[];
}): NCNode[] => {
	const graph = globalThis.neuroCreate.graph;
	if (!graph) return [];
	const labels = new Set(
		(selectedNodeLabels || []).map((label) => label.trim().toLowerCase()).filter(Boolean)
	);
	const ids = new Set((selectedNodeIds || []).map((id) => id.trim()).filter(Boolean));
	const selected = graph.nodes.filter((node) => {
		const uid = node.uid || '';
		const label = (node.label || '').trim().toLowerCase();
		return (uid && ids.has(uid)) || (label && labels.has(label));
	});
	if (selected.length) return selected;
	return graph.activeNode ? [graph.activeNode] : [];
};

export const showBackendGenerateResults = ({
	task,
	tool,
	results,
	selectedNodeIds,
	selectedNodeLabels,
	recommendedResultId,
	recommendedResultIds,
}: {
	task?: string | null;
	tool?: string | null;
	results: BackendToolResultEntry[];
	selectedNodeIds?: string[];
	selectedNodeLabels?: string[];
	recommendedResultId?: string | null;
	recommendedResultIds?: string[];
}) => {
	const generateTask = generateTaskFromBackendTask(task, tool);
	if (!generateTask) return null;
	const selectedNodes = resolveBackendSelectedNodes({ selectedNodeIds, selectedNodeLabels });
	const node = selectedNodes[0] || globalThis.neuroCreate.graph?.activeNode || null;
	if (node) {
		toggleToolContainer('generate', node);
		resetGenerate(
			generateTask,
			fetchGenerateResults.bind(null, selectedNodes.length ? selectedNodes : [node]),
			node
		);
	} else {
		document.querySelector('#generate-container')?.classList.remove('hidden');
		applyGenerateTaskButtons({ selectedTask: generateTask });
		removeChildren(document.querySelector('#generate-container .results-list')!);
		document.querySelector('#generate-container .loading')?.classList.add('hidden');
	}
	return showGenerateResults(
		generateTask,
		normalizeBackendResultEntries(generateTask, results),
		false,
		selectedNodes,
		{ suppressTutorialEvent: true, recommendedResultId, recommendedResultIds }
	);
};

const emitRenderedGenerateResults = (task: GenerateTask, entries: GenerateResultEntry[]) => {
	if (task === 'brainstorm') {
		tutorialEvents.brainstormResultsGenerated(entries);
	} else if (task === 'common') {
		tutorialEvents.commonIdeasResultsGenerated(entries);
	} else if (task === 'persona') {
		tutorialEvents.personaGenerated(entries);
	} else if (task === 'motivations') {
		tutorialEvents.motivationsGenerated(entries);
	} else if (task === 'ai-haiku') {
		tutorialEvents.aiHaikusGenerated(entries);
	} else if (task === 'haiku') {
		tutorialEvents.haikusGenerated(entries);
	} else if (task === 'image-inspire') {
		tutorialEvents.imageInspireGenerated(entries);
	} else if (task === 'video-inspire') {
		tutorialEvents.videoInspireGenerated(entries);
	} else if (task === 'image-synthesise') {
		tutorialEvents.imageSynthesiseGenerated(entries);
	} else if (task === 'proverb') {
		tutorialEvents.taglinesGenerated(entries);
	} else if (task === 'story') {
		tutorialEvents.storiesGenerated(entries);
	} else if (task === 'character') {
		tutorialEvents.charactersGenerated(entries);
	} else if (task === 'brand-colours') {
		tutorialEvents.brandColoursGenerated(entries);
	} else if (task === 'brand-tone') {
		tutorialEvents.brandToneGenerated(entries);
	} else if (task === 'audience') {
		tutorialEvents.audiencesGenerated(entries);
	}
};

export const openBackendGeneratePanel = ({
	task,
	tool,
	selectedNodeIds,
	selectedNodeLabels,
	showLoading = false,
}: {
	task?: string | null;
	tool?: string | null;
	selectedNodeIds?: string[];
	selectedNodeLabels?: string[];
	showLoading?: boolean;
}) => {
	const generateTask = generateTaskFromBackendTask(task, tool);
	const selectedNodes = resolveBackendSelectedNodes({ selectedNodeIds, selectedNodeLabels });
	const node = selectedNodes[0] || globalThis.neuroCreate.graph?.activeNode || null;
	if (node) {
		toggleToolContainer('generate', node);
		if (generateTask) {
			resetGenerate(
				generateTask,
				fetchGenerateResults.bind(null, selectedNodes.length ? selectedNodes : [node]),
				node
			);
		} else {
			applyGenerateTaskButtons();
		}
	} else {
		document.querySelector('#generate-container')?.classList.remove('hidden');
		applyGenerateTaskButtons({ selectedTask: generateTask });
	}
	if (showLoading) {
		document.querySelector('#generate-container .loading')?.classList.remove('hidden');
		startBackendGenerateProgress(generateTask);
	}
	return Boolean(generateTask);
};

export const highlightGenerateResult = ({
	resultId,
	resultIds,
	resultIndex,
	label,
}: {
	resultId?: string | null;
	resultIds?: string[] | null;
	resultIndex?: number | null;
	label?: string | null;
}) => {
	const container = document.querySelector('#generate-container');
	if (!container) return false;
	const normalizedLabel = (label || '').trim().toLowerCase();
	const resultIdSet = new Set([...(resultId ? [resultId] : []), ...(resultIds || [])]);
	let matched = false;
	container.querySelectorAll<HTMLLIElement>('.results-list li').forEach((item) => {
		const title = (item.dataset.resultTitle || '').trim().toLowerCase();
		const snippet = (item.dataset.resultSnippet || '').trim().toLowerCase();
		const idMatches = Boolean(item.dataset.resultId && resultIdSet.has(item.dataset.resultId));
		const indexMatches = Boolean(
			typeof resultIndex === 'number' && item.dataset.resultIndex === String(resultIndex)
		);
		const labelMatches = Boolean(
			normalizedLabel &&
				(title === normalizedLabel ||
					snippet === normalizedLabel ||
					title.includes(normalizedLabel) ||
					snippet.includes(normalizedLabel) ||
					normalizedLabel.includes(title) ||
					normalizedLabel.includes(snippet))
		);
		const isMatch = indexMatches || idMatches || labelMatches;
		item.classList.toggle('flowy-recommended-result', isMatch);
		if (isMatch && !matched) {
			matched = true;
			highlightFlowyTarget({
				target: item,
				label: 'Flowy pick',
			});
		}
	});
	return matched;
};

const listGenerateResultItems = (): HTMLLIElement[] =>
	Array.from(document.querySelectorAll<HTMLLIElement>('#generate-container .results-list li'));

/** Heart Flowy's highlighted (or first) Generate panel result into the Idea Box. */
export const likeFlowyGenerateResult = (): boolean => {
	const item = pickGenerateResultItem(listGenerateResultItems());
	if (!item) return false;
	document.querySelector('#generate-container')?.classList.remove('hidden');
	return likeGenerateResultItem(item);
};

/** Place Flowy's highlighted (or first) Generate panel media onto the Inspire board. */
export const addFlowyGenerateResultToBoard = (): boolean => {
	const item = pickGenerateResultItem(listGenerateResultItems());
	if (!item) return false;
	document.querySelector('#generate-container')?.classList.remove('hidden');
	return addGenerateResultItemToBoard(item);
};

let currentRequestId: string | undefined;
let currentPositioningGenerationAbortController: AbortController | undefined;

type PositioningGenerateTask =
	| 'persona'
	| 'character'
	| 'motivations'
	| 'brainstorm'
	| 'proverb'
	| 'story'
	| 'brand-colours'
	| 'brand-tone';

const positioningTaskLabels: Record<PositioningGenerateTask, string> = {
	persona: 'Persona',
	character: 'Character',
	motivations: 'Stakeholder motivation',
	brainstorm: 'Solution ideas',
	proverb: 'Taglines',
	story: 'Story',
	'brand-colours': 'Brand colours',
	'brand-tone': 'Tone of voice',
};

const applyGenerateTaskButtons = ({
	selectedTask,
	positioningMode,
}: {
	selectedTask?: GenerateTask | null;
	positioningMode?: boolean;
} = {}) => {
	const container = document.querySelector<HTMLElement>('#generate-container');
	if (!container) return;
	const graph = globalThis.neuroCreate.graph;
	const graphMode = graph?.graphMode || getGraphModeFromHash()[0];
	const activeAnalysis =
		graphMode === 'ideate' ? (graph as IdeateGraph).activeAnalysis || null : null;
	const inPositioningMode = positioningMode ?? container.dataset.positioningMode === 'true';
	container.querySelectorAll<HTMLButtonElement>('.select-option button').forEach((button) => {
		const task = button.dataset.task as GenerateTask;
		const visible = isGenerateTaskVisible({
			task,
			graphMode,
			positioningMode: inPositioningMode,
			activeAnalysis,
		});
		button.classList.toggle('hidden', !visible);
		if (!inPositioningMode) {
			button.dataset.positioningLane = '';
		}
		if (task === 'brainstorm') {
			button.innerText = graphMode === 'inspire' ? 'Brainstorm' : positioningTaskLabels.brainstorm;
		}
		if (selectedTask) {
			button.classList.toggle('selected', visible && task === selectedTask);
		}
	});
	updateGenerateTooltip(container, graphMode);
};

const POSITIONING_LIVE_GROUNDING_TASKS = new Set<PositioningGenerateTask>([
	'brand-colours',
	'brand-tone',
	'persona',
	'character',
	'motivations',
	'brainstorm',
	'story',
]);

const positioningRegenerationLabels: Record<PositioningGenerateTask, string> = {
	persona: 'personas',
	character: 'characters',
	motivations: 'motivations',
	brainstorm: 'solution ideas',
	proverb: 'taglines',
	story: 'stories',
	'brand-colours': 'brand colours',
	'brand-tone': 'tone of voice',
};

const positioningRegenerationOverlay = (): HTMLElement => {
	let overlay = document.querySelector<HTMLElement>('#positioning-regeneration-overlay');
	if (overlay) return overlay;
	overlay = document.createElement('section');
	overlay.id = 'positioning-regeneration-overlay';
	overlay.className = 'positioning-regeneration-overlay hidden';
	overlay.setAttribute('role', 'status');
	overlay.setAttribute('aria-live', 'assertive');
	overlay.setAttribute('aria-busy', 'true');
	const card = document.createElement('div');
	card.className = 'positioning-regeneration-overlay__card';
	const spinner = document.createElement('span');
	spinner.className = 'positioning-regeneration-overlay__spinner';
	spinner.setAttribute('aria-hidden', 'true');
	const title = document.createElement('h2');
	card.append(spinner, title);
	overlay.append(card);
	document.body.append(overlay);
	return overlay;
};

const showPositioningRegenerationOverlay = (task: PositioningGenerateTask): void => {
	const overlay = positioningRegenerationOverlay();
	overlay.querySelector('h2')!.innerText = `Generating new ${positioningRegenerationLabels[task]}…`;
	overlay.classList.remove('hidden', 'is-returning');
	overlay.setAttribute('aria-busy', 'true');
};

const hidePositioningRegenerationOverlay = (): void => {
	const overlay = document.querySelector<HTMLElement>('#positioning-regeneration-overlay');
	overlay?.classList.add('hidden');
	overlay?.classList.remove('is-returning');
	overlay?.setAttribute('aria-busy', 'false');
};

const finishPositioningRegeneration = async (): Promise<void> => {
	const overlay = positioningRegenerationOverlay();
	overlay.querySelector('h2')!.innerText = 'Returning to positioning map';
	overlay.classList.add('is-returning');
	overlay.setAttribute('aria-busy', 'false');
	await new Promise<void>((resolve) => window.setTimeout(resolve, 700));
	window.dispatchEvent(new CustomEvent('positioning-return-to-map'));
	hidePositioningRegenerationOverlay();
};

const preferredAnalysisTerms: Record<PositioningGenerateTask, string[]> = {
	persona: ['personality', 'archetype', 'greek', 'themes'],
	character: ['archetype', 'plots', 'personality', 'themes'],
	motivations: ['motivation', 'personality', 'greek', 'themes'],
	brainstorm: ['themes', 'archetype', 'plots', 'random'],
	proverb: ['personality', 'themes', 'archetype', 'plots'],
	story: ['plots', 'archetype', 'themes', 'personality'],
	'brand-colours': ['personality', 'themes', 'archetype', 'greek'],
	'brand-tone': ['personality', 'archetype', 'themes', 'greek'],
};

const getDefaultPositioningAnalyses = (
	task: PositioningGenerateTask,
	analyses: AnalysisKey[]
): AnalysisKey[] => {
	const preferences = preferredAnalysisTerms[task];
	return [...analyses]
		.sort((a, b) => {
			const aLabel =
				`${a} ${ANALYSES_TYPES[a]?.name || ''} ${ANALYSES_TYPES[a]?.category || ''}`.toLowerCase();
			const bLabel =
				`${b} ${ANALYSES_TYPES[b]?.name || ''} ${ANALYSES_TYPES[b]?.category || ''}`.toLowerCase();
			const score = (label: string) => {
				const index = preferences.findIndex((preference) => label.includes(preference));
				return index < 0 ? preferences.length : index;
			};
			return score(aLabel) - score(bLabel);
		})
		.slice(0, Math.min(3, analyses.length));
};

const positioningPerspectives = [
	{
		name: 'Primary audience fit',
		description:
			'Prioritises the strongest needs and most direct route to relevance without defaulting to a generic young professional.',
	},
	{
		name: 'Productive tension',
		description:
			'Combines contrasting signals to expose a different life stage, barrier, trade-off and unmet need.',
	},
	{
		name: 'Adjacent possibility',
		description:
			'Uses less obvious analysis signals to represent an indirect participant or adjacent stakeholder.',
	},
	{
		name: 'Underserved edge',
		description:
			'Centres someone constrained by access, ability, resources, geography or exclusion whose needs alter the design.',
	},
	{
		name: 'Sceptic and blocker',
		description:
			'Explores a cautious non-adopter, gatekeeper or blocker with a distinct objection and proof threshold.',
	},
	{
		name: 'Frontline reality',
		description:
			'Focuses on an operator or practitioner living with practical workflow, time and accountability pressures.',
	},
	{
		name: 'Power and influence',
		description:
			'Examines a decision-maker, funder, buyer or informal influencer whose motivation differs from the end user.',
	},
	{
		name: 'Life transition',
		description:
			'Looks through a contrasting age or life-stage transition that changes the need, trigger and support network.',
	},
	{
		name: 'Community context',
		description:
			'Prioritises collective, cultural or family context rather than an individualised version of the same need.',
	},
];

const buildPositioningCombinations = (
	task: PositioningGenerateTask,
	projectVision: string,
	analysisRuns: Array<{ analysis: AnalysisKey; clusters: NCCluster[] }>,
	variationOffset = 0
): PositioningGenerationContext[] =>
	Array.from({ length: Math.min(3, positioningPerspectives.length) }, (_, index) => ({
		perspective:
			positioningPerspectives[(variationOffset + index) % positioningPerspectives.length],
		perspectiveIndex: variationOffset + index,
	}))
		.map(({ perspective, perspectiveIndex }) => {
			const sources = analysisRuns.flatMap<PositioningAnalysisSource>(
				({ analysis, clusters }, analysisIndex) => {
					if (clusters.length === 0) return [];
					const clusterIndex =
						(perspectiveIndex * (analysisIndex + 1) + analysisIndex) % clusters.length;
					const cluster = clusters[clusterIndex];
					const values = cluster.values.filter(Boolean);
					const nodeCount = Math.min(3, values.length);
					const start = values.length ? (perspectiveIndex + analysisIndex * 2) % values.length : 0;
					const nodes = Array.from({ length: nodeCount }, (_, offset) => {
						return values[(start + offset * (perspectiveIndex + 1)) % values.length];
					}).filter((value, index, selected) => selected.indexOf(value) === index);
					return [
						{
							analysis,
							analysisName: ANALYSES_TYPES[analysis]?.name || analysis,
							cluster: cluster.group,
							nodes,
						},
					];
				}
			);
			return {
				task,
				perspective: perspective.name,
				perspectiveDescription: perspective.description,
				projectVision,
				sources,
				analysisSnapshots: analysisRuns.map((run) => ({
					analysis: run.analysis,
					clusters: run.clusters.map((cluster) => ({
						group: cluster.group,
						values: [...cluster.values],
					})),
				})),
			};
		})
		.filter((context) => context.sources.length > 0);

const buildPositioningGenerationPrompt = (
	context: PositioningGenerationContext,
	sourceContext: PositioningSourceContext
) => {
	const normalizedSources = context.sources.map(ensurePositioningClusterCenter);
	const analysisContext = normalizedSources
		.map(
			(source) =>
				`${source.analysisName} > ${source.cluster}: ${source.nodes.join(', ') || source.cluster}`
		)
		.join('\n');
	const evidenceTerms = normalizedSources.flatMap((source) => [source.cluster, ...source.nodes]);
	const evidenceTokens = new Set(
		evidenceTerms
			.flatMap((term) => term.toLocaleLowerCase().match(/[\p{L}\p{N}]+/gu) || [])
			.filter((term) => term.length > 2)
	);
	const relevantPdfEvidence = sourceContext.pdfTopics
		.map((entry, index) => {
			const tokens =
				`${entry.topic} ${entry.extractedText}`.toLocaleLowerCase().match(/[\p{L}\p{N}]+/gu) || [];
			const score = new Set(tokens.filter((token) => evidenceTokens.has(token))).size;
			return { entry, index, score };
		})
		.filter(({ score }) => score > 0)
		.sort((a, b) => b.score - a.score || a.index - b.index)
		.slice(0, 3)
		.map(({ entry }) => {
			const excerpt = entry.extractedText.replace(/\s+/g, ' ').trim().slice(0, 320);
			return `- ${entry.topic}${excerpt ? `: ${excerpt}` : ''}`;
		});
	const socialBlueprint = context.socialPerspectiveBlueprint;
	return [
		`PROJECT FRAMING: ${sourceContext.projectFraming || context.projectVision || 'Not provided'}`,
		sourceContext.pdfOverview ? `PDF OVERVIEW: ${sourceContext.pdfOverview}` : '',
		relevantPdfEvidence.length ? `RELEVANT PDF EVIDENCE:\n${relevantPdfEvidence.join('\n')}` : '',
		`CREATIVE PERSPECTIVE: ${context.perspective}. ${context.perspectiveDescription || ''}`,
		`PSYCHOGRAPHIC ANALYSIS INPUTS:\n${analysisContext}`,
		socialBlueprint
			? `SOCIAL PERSPECTIVE ASSIGNMENT: observed stance ${socialBlueprint.primaryStance};${
				socialBlueprint.sentiment ? ` sentiment ${socialBlueprint.sentiment};` : ''
			} evidence lane ${socialBlueprint.primarySegmentId}.`
			: '',
		'Use the analysis inputs as the specific basis for the output. Synthesize them into one coherent direction rather than repeating their labels. Make this result materially different from other perspectives.',
		'Each persona or audience result must reflect a distinct audience segment. The project objective is not this perspective\'s position: when a social perspective assignment is present, represent its observed stance accurately and respectfully. Do not invent a missing stance, demographic or health claim, and do not treat the project goal or PDF recommendation as evidence that this person agrees with it.',
	]
		.filter(Boolean)
		.join('\n\n');
};

const generatePositioningResults = async ({
	task,
	selectedAnalyses,
	sourceContext,
	configuredContext,
	variationOffset = 0,
	onProgress,
	signal,
	skipLiveSources = false,
}: {
	task: PositioningGenerateTask;
	selectedAnalyses: AnalysisKey[];
	sourceContext: PositioningSourceContext;
	configuredContext?: PositioningGenerationContext;
	variationOffset?: number;
	signal?: AbortSignal;
	skipLiveSources?: boolean;
	onProgress?: (
		stage: 'analyses' | 'sources' | 'social' | 'grounding' | 'generating',
		detail: string
	) => void;
}): Promise<GenerateResultEntry[]> => {
	const projectVision = sourceContext.projectFraming || configuredContext?.projectVision || '';
	let contexts: PositioningGenerationContext[];
	if (configuredContext) {
		contexts = [
			{
				...configuredContext,
				task,
				perspective: 'Configured psychographic basis',
				perspectiveDescription: `Regenerated from the exact cross-analysis selection configured on the board. Produce alternative ${variationOffset + 1} with a materially different person, need, motivation and implication.`,
				projectVision,
				sources: configuredContext.sources
					.filter((source) => selectedAnalyses.includes(source.analysis))
					.map(ensurePositioningClusterCenter),
			},
		].filter((context) => context.sources.length > 0);
	} else {
		const analysisInput =
			sourceContext.analysisTerms.join(' ').trim() ||
			projectVision ||
			(sourceContext.pdfOverview || '').trim();
		if (!analysisInput) {
			throw new Error(currentIdeateMissingSourceMessage());
		}
		let completedAnalyses = 0;
		onProgress?.('analyses', `Starting ${selectedAnalyses.length} selected analyses…`);
		const settledAnalysisRuns = await Promise.allSettled(
			selectedAnalyses.map(async (analysis) => {
				const clusters = await ncApi.clusters(
					document.body.dataset.boardId!,
					analysis,
					analysisInput,
					false,
					signal
				);
				completedAnalyses += 1;
				onProgress?.(
					'analyses',
					`${ANALYSES_TYPES[analysis]?.name || analysis} complete · ${completedAnalyses}/${selectedAnalyses.length}`
				);
				return { analysis, clusters };
			})
		);
		const analysisRuns = settledAnalysisRuns.flatMap((result) =>
			result.status === 'fulfilled' ? [result.value] : []
		);
		if (analysisRuns.length === 0) {
			throw new Error('The selected analyses did not return any results.');
		}
		onProgress?.(
			'sources',
			`Found ${analysisRuns.reduce((total, run) => total + run.clusters.length, 0)} clusters across Inspire and selected PDF context.`
		);
		contexts = buildPositioningCombinations(task, projectVision, analysisRuns, variationOffset);
	}
	if (contexts.length === 0) return [];
	const groundingSearchTerm = [
		projectVision,
		...contexts.flatMap((context) =>
			context.sources.flatMap((source) => [source.cluster, ...source.nodes.slice(0, 4)])
		),
	]
		.filter(Boolean)
		.join(' ')
		.replace(/\s+/g, ' ')
		.trim()
		.slice(0, 800);
	const useLiveSources = !skipLiveSources && POSITIONING_LIVE_GROUNDING_TASKS.has(task);
	const sharedPositioningContext = contexts[0];
	onProgress?.(
		'sources',
		!useLiveSources
			? 'Gathering board grounding for all perspectives…'
			: 'Searching live grounding once for all perspectives…'
	);
	const cloneSnapshot = (
		snapshot: IdeateGroundingSnapshot | undefined
	): IdeateGroundingSnapshot | undefined => {
		if (!snapshot) return undefined;
		return {
			framing: snapshot.framing?.map((item) => ({ ...item })),
			boardConcepts: snapshot.boardConcepts?.map((item) => ({ ...item })),
			likedResearch: snapshot.likedResearch?.map((item) => ({ ...item })),
			flowy: snapshot.flowy?.map((item) => ({ ...item })),
			socialProfiles: snapshot.socialProfiles?.map((item) => ({ ...item })),
			socialSegments: snapshot.socialSegments?.map((segment) => ({ ...segment })),
			selectedSocialSegmentIds: [...(snapshot.selectedSocialSegmentIds || [])],
			selectedFraming: (snapshot.selectedFraming ?? snapshot.framing)?.map((item) => ({
				...item,
			})),
			selectedBoardConcepts: (snapshot.selectedBoardConcepts ?? snapshot.boardConcepts)?.map(
				(item) => ({ ...item })
			),
			selectedLikedResearch: (snapshot.selectedLikedResearch ?? snapshot.likedResearch)?.map(
				(item) => ({ ...item })
			),
			selectedSocialProfiles: (snapshot.selectedSocialProfiles ?? snapshot.socialProfiles)?.map(
				(item) => ({ ...item })
			),
		};
	};
	let sharedGroundingEvidence = configuredContext?.groundingEvidence?.map((evidence) => ({
		...evidence,
	}));
	let sharedGroundingSnapshot = cloneSnapshot(configuredContext?.groundingSnapshot);
	if (sharedGroundingEvidence === undefined || sharedGroundingSnapshot === undefined) {
		const collected = await ncApi.collectGenerateGrounding(
			document.body.dataset.boardId!,
			task,
			groundingSearchTerm,
			signal,
			{
				skipLiveSources: !useLiveSources,
				positioningContext: sharedPositioningContext,
				onProgress: (effect) => {
					const stage =
						effect.stage === 'social'
							? 'social'
							: effect.stage === 'grounding'
								? 'grounding'
								: 'sources';
					const sourceDetail = effect.sources?.length
						? ` ${effect.sources.map((source) => `${source.source}: ${source.title}`).join(' · ')}`
						: '';
					onProgress?.(stage, `${effect.detail}${sourceDetail}`);
				},
			}
		);
		if (sharedGroundingEvidence === undefined) {
			sharedGroundingEvidence = useLiveSources ? collected.evidence : [];
		}
		if (sharedGroundingSnapshot === undefined) {
			sharedGroundingSnapshot = cloneSnapshot(collected.groundingSnapshot) || {
				boardConcepts: [],
				likedResearch: [],
				framing: [],
				flowy: [],
				socialProfiles: [],
				selectedFraming: [],
				selectedBoardConcepts: [],
				selectedLikedResearch: [],
				selectedSocialProfiles: [],
			};
		}
	}
	const sharedProfiles =
		sharedGroundingSnapshot?.selectedSocialProfiles ??
		sharedGroundingSnapshot?.socialProfiles ??
		(sharedGroundingEvidence || []).filter(isSocialPosterProfileEvidence);
	contexts = contexts.map((context, perspectiveIndex) => {
		const assignment = assignSocialPerspectiveBlueprint(
			sharedProfiles,
			perspectiveIndex,
			contexts.length
		);
		const assignedProfiles = assignment.evidence;
		const nonProfileEvidence = (sharedGroundingEvidence || []).filter(
			(evidence) => !isSocialPosterProfileEvidence(evidence)
		);
		const perspectiveSnapshot = cloneSnapshot(sharedGroundingSnapshot);
		if (perspectiveSnapshot) {
			perspectiveSnapshot.selectedSocialProfiles = assignedProfiles.map((item) => ({ ...item }));
		}
		return {
			...context,
			socialPerspectiveBlueprint: assignment.blueprint,
			groundingEvidence: [
				...nonProfileEvidence.map((evidence) => ({ ...evidence })),
				...assignedProfiles.map((item) => ({ ...item })),
			],
			groundingSnapshot: perspectiveSnapshot,
		};
	});
	const boardSignalCount =
		(sharedGroundingSnapshot?.selectedBoardConcepts ?? sharedGroundingSnapshot?.boardConcepts ?? [])
			.length +
		(sharedGroundingSnapshot?.selectedLikedResearch ?? sharedGroundingSnapshot?.likedResearch ?? [])
			.length;
	onProgress?.(
		'grounding',
		(sharedGroundingEvidence || []).length > 0
			? `Assigning balanced evidence to 0/${contexts.length} perspectives from ${sharedGroundingEvidence!.length} live source signals${
					boardSignalCount ? ` and ${boardSignalCount} board signals` : ''
				}.`
			: boardSignalCount > 0
				? `Assigning board grounding to 0/${contexts.length} perspectives from ${boardSignalCount} board signals.`
				: useLiveSources
					? `Assigning evidence to 0/${contexts.length} perspectives from board and PDF context; live sources were unavailable or returned no evidence.`
					: `Assigning evidence to 0/${contexts.length} perspectives from board and PDF context.`
	);
	const groundedPerspectives = new Set<number>();
	const generatingPerspectives = new Set<number>();
	let completedPerspectives = 0;
	const generated = await Promise.allSettled(
		contexts.map(async (context, index) => {
			try {
				const entries = await ncApi.generateWithProgress(
					document.body.dataset.boardId!,
					task,
					buildPositioningGenerationPrompt(context, sourceContext),
					1,
					(effect) => {
						if (effect.stage === 'social') {
							onProgress?.('social', effect.detail);
						}
						if (effect.stage === 'grounding') {
							groundedPerspectives.add(index);
							onProgress?.(
								'grounding',
								`Assigned balanced evidence to ${groundedPerspectives.size}/${contexts.length} perspectives.`
							);
						}
						if (effect.stage === 'generating') {
							generatingPerspectives.add(index);
							onProgress?.(
								'generating',
								`Generating ${generatingPerspectives.size}/${contexts.length} perspectives.`
							);
						}
						const stage =
							effect.stage === 'social'
								? 'social'
								: effect.stage === 'grounding'
									? 'grounding'
									: effect.stage === 'generating'
										? 'generating'
										: 'sources';
						const sourceDetail = effect.sources?.length
							? ` ${effect.sources
									.map((source) => `${source.source}: ${source.title}`)
									.join(' · ')}`
							: '';
						if (
							effect.stage !== 'grounding' &&
							effect.stage !== 'generating' &&
							effect.stage !== 'social'
						) {
							onProgress?.(stage, `${effect.detail}${sourceDetail}`);
						}
					},
					{
						signal,
						groundingEvidence: context.groundingEvidence || [],
						// Analysis Explorer regeneration already carries the selected
						// analyses and grounding snapshot. Do not make it wait for a
						// second social/web collection before the one replacement LLM call.
						skipLiveSources: skipLiveSources || Boolean(configuredContext),
						positioningContext: context,
					}
				);
				const entry = entries[0];
				return entry
					? {
							...entry,
							id: `positioning-${task}-${Date.now()}-${index}`,
							positioningContext: context,
						}
					: null;
			} finally {
				completedPerspectives += 1;
				onProgress?.(
					'generating',
					`Completed ${completedPerspectives}/${contexts.length} perspectives.`
				);
			}
		})
	);
	const successful = generated.flatMap((result) =>
		result.status === 'fulfilled' && result.value ? [result.value] : []
	);
	const failedCount = generated.length - successful.length;
	if (failedCount > 0) {
		console.warn('[positioning-generation] some perspectives failed', {
			task,
			failedCount,
			successfulCount: successful.length,
		});
	}
	return successful;
};

export const openPositioningGeneratePanel = ({
	task,
	analyses: requestedAnalyses,
	sourceContext,
	contextLabel,
	configuredContext,
	autoGenerate = false,
}: {
	task: PositioningGenerateTask;
	analyses: AnalysisKey[];
	sourceContext: PositioningSourceContext;
	contextLabel: string;
	configuredContext?: PositioningGenerationContext;
	autoGenerate?: boolean;
}) => {
	const analyses = analysesForPositioningGenerate(requestedAnalyses);
	if (!configuredContext && !hasPositioningSourceContext(sourceContext)) {
		showAlert(currentIdeateMissingSourceMessage());
		return;
	}
	const container = document.querySelector<HTMLElement>('#generate-container');
	if (!container) return;

	closeAiContainers('generate');
	container.classList.remove('hidden');
	container.dataset.positioningMode = 'true';
	const positioningConfig = container.querySelector<HTMLDetailsElement>(
		'.positioning-generate-context'
	);
	positioningConfig?.classList.remove('hidden');
	if (positioningConfig) positioningConfig.open = true;
	container.querySelector('.common-ideas-input')?.classList.add('hidden');
	container.querySelector('.brainstorm-input')?.classList.add('hidden');
	container.querySelector('.ideate-options')?.classList.add('hidden');
	container.querySelector('.loading')?.classList.add('hidden');
	removeChildren(container.querySelector('.results-list')!);
	const moreButton = container.querySelector<HTMLButtonElement>('.positioning-generate-more')!;
	moreButton.classList.add('hidden');
	moreButton.disabled = false;
	moreButton.innerText = 'Generate more results';
	hideGenerateProgress();
	const updateProgress = (
		stage: 'analyses' | 'sources' | 'social' | 'grounding' | 'generating',
		detail: string
	) => {
		updateGenerateProgress(stage, detail);
	};

	const breadcrumb = container.querySelector<HTMLElement>('.breadcrumbs li.node-title');
	if (breadcrumb) breadcrumb.innerText = contextLabel;
	const title = container.querySelector<HTMLElement>('#positioning-generate-title');
	if (title) title.innerText = `Generate ${positioningTaskLabels[task]}`;

	const taskButtons = container.querySelectorAll<HTMLButtonElement>('.select-option button');
	applyGenerateTaskButtons({ selectedTask: task, positioningMode: true });
	const taskLanes: Partial<Record<GenerateTask, string>> = {
		persona: 'audience',
		motivations: 'motivation',
		brainstorm: 'solution',
		proverb: 'brand',
		'brand-colours': 'brand',
		'brand-tone': 'brand',
		story: 'story',
		character: 'story',
	};
	taskButtons.forEach((button) => {
		const buttonTask = button.dataset.task as GenerateTask;
		button.dataset.positioningLane = taskLanes[buttonTask] || '';
		button.onclick = POSITIONING_GENERATE_TASKS.includes(buttonTask)
			? () =>
					openPositioningGeneratePanel({
						task: buttonTask as PositioningGenerateTask,
						analyses,
						sourceContext,
						contextLabel,
					})
			: null;
	});

	const selectedByDefault = new Set(
		configuredContext?.sources.map((source) => source.analysis) ||
			getDefaultPositioningAnalyses(task, analyses)
	);
	const analysisOptions = container.querySelector<HTMLFieldSetElement>(
		'.positioning-analysis-options'
	)!;
	analysisOptions.querySelectorAll(':scope > label').forEach((label) => label.remove());

	analyses.forEach((analysisKey) => {
		const input = document.createElement('input');
		input.type = 'checkbox';
		input.id = `positioning-generate-${task}-${analysisKey}`;
		input.value = analysisKey;
		input.checked = selectedByDefault.has(analysisKey);
		const label = document.createElement('label');
		label.htmlFor = input.id;
		label.append(input, document.createTextNode(ANALYSES_TYPES[analysisKey]?.name || analysisKey));
		analysisOptions.append(label);
	});

	const submit = container.querySelector<HTMLButtonElement>('.positioning-generate-submit')!;
	const syncSubmit = () => {
		const count = analysisOptions.querySelectorAll<HTMLInputElement>('input:checked').length;
		const compact = container.querySelector<HTMLElement>('.positioning-generate-compact');
		if (compact) compact.innerText = `${count} ${count === 1 ? 'analysis' : 'analyses'} selected`;
		submit.disabled = count === 0;
		submit.innerText = `Generate ${positioningTaskLabels[task]} with ${count} ${
			count === 1 ? 'analysis' : 'analyses'
		}`;
	};
	analysisOptions.querySelectorAll('input').forEach((input) => {
		input.addEventListener('change', syncSubmit);
	});
	syncSubmit();

	let variationOffset = 0;
	const runGeneration = (replaceResults: boolean, skipLiveSources = false) => {
		const selectedAnalyses = Array.from(
			analysisOptions.querySelectorAll<HTMLInputElement>('input:checked')
		).map((input) => input.value as AnalysisKey);
		if (selectedAnalyses.length === 0) return;
		if (positioningConfig) positioningConfig.open = false;

		currentPositioningGenerationAbortController?.abort();
		const abortController = new AbortController();
		currentPositioningGenerationAbortController = abortController;
		const requestId = uuidv4();
		currentRequestId = requestId;
		let timedOut = false;
		const isRegeneration = autoGenerate && Boolean(configuredContext) && replaceResults;
		if (isRegeneration) showPositioningRegenerationOverlay(task);
		if (replaceResults) {
			variationOffset = 0;
			removeChildren(container.querySelector('.results-list')!);
			moreButton.classList.add('hidden');
		}
		const requestVariationOffset = variationOffset;
		variationOffset += 3;
		moreButton.disabled = true;
		moreButton.innerText = 'Generating more results…';
		container.querySelector('.loading')?.classList.remove('hidden');
		configureGenerateProgress({
			owner: 'positioning',
			title: 'Building grounded outputs',
			includeSocial: task === 'persona' || task === 'motivations',
		});
		updateProgress('analyses', 'Preparing the selected analyses…');
		const showDeadlineRecovery = () =>
			showPositioningGenerationRecovery({
				onRetry: () => runGeneration(replaceResults),
				onContinueWithoutSources: () => runGeneration(replaceResults, true),
			});
		const deadlineTimer = window.setTimeout(() => {
			if (currentRequestId !== requestId) return;
			timedOut = true;
			abortController.abort();
			if (isRegeneration) hidePositioningRegenerationOverlay();
			container.querySelector('.loading')?.classList.add('hidden');
			showDeadlineRecovery();
		}, POSITIONING_GENERATION_CLIENT_DEADLINE_MS);

		generatePositioningResults({
			task,
			selectedAnalyses,
			sourceContext,
			configuredContext,
			variationOffset: requestVariationOffset,
			onProgress: updateProgress,
			signal: abortController.signal,
			skipLiveSources,
		})
			.then(async (entries) => {
				if (currentRequestId === requestId) {
					if (entries.length > 0) {
						const renderedEntries = showGenerateResults(task, entries);
						emitRenderedGenerateResults(task, renderedEntries);
						if (timedOut) showDeadlineRecovery();
						else if (isRegeneration) await finishPositioningRegeneration();
					} else if (timedOut) {
						if (isRegeneration) hidePositioningRegenerationOverlay();
						showDeadlineRecovery();
					} else {
						if (isRegeneration) hidePositioningRegenerationOverlay();
						showGenerateError();
					}
				}
			})
			.catch((error: unknown) => {
				if (isRegeneration) hidePositioningRegenerationOverlay();
				if (
					timedOut ||
					(error instanceof DOMException && error.name === 'AbortError') ||
					(error instanceof Error && error.name === 'AbortError')
				) {
					return;
				}
				const missingSource = currentIdeateMissingSourceMessage();
				const message =
					error instanceof Error && error.message
						? error.message === missingSource ||
							/social poster|Social listening|API Direct/i.test(error.message)
							? error.message
							: undefined
						: undefined;
				showGenerateError(message);
			})
			.finally(() => {
				window.clearTimeout(deadlineTimer);
				if (currentRequestId === requestId) {
					moreButton.disabled = false;
					moreButton.innerText = 'Generate more results';
					if (currentPositioningGenerationAbortController === abortController) {
						currentPositioningGenerationAbortController = undefined;
					}
				}
			});
	};
	submit.onclick = () => runGeneration(true);
	moreButton.onclick = () => runGeneration(false);
	if (autoGenerate) runGeneration(true);
};

window.addEventListener('positioning-generate-request', (event) => {
	const detail = (
		event as CustomEvent<{
			task: PositioningGenerateTask;
			analyses: AnalysisKey[];
			sourceContext: PositioningSourceContext;
			contextLabel: string;
			configuredContext?: PositioningGenerationContext;
			autoGenerate?: boolean;
		}>
	).detail;
	if (detail) openPositioningGeneratePanel(detail);
});

export const fetchGenerateResults = (
	nodes: Array<NCNode>,
	task: GenerateTask,
	reset = true
): Promise<void> => {
	if (reset) {
		resetGenerate(task, fetchGenerateResults.bind(null, nodes), nodes[0]);
	}

	let inputString = nodes.map((node) => node.label).join(' ');

	inputString = getIdeateTaskString(task, nodes, inputString);

	const requestId = uuidv4();
	currentRequestId = requestId;
	const showInspireProgress = globalThis.neuroCreate.graph?.graphMode === 'inspire';
	if (showInspireProgress) {
		const taskLabel = task.replace(/-/g, ' ');
		configureGenerateProgress({
			owner: 'inspire',
			title: `Generating ${taskLabel}`,
			includeSocial: task === 'persona' || task === 'motivations',
		});
		updateGenerateProgress('searching', 'Preparing the generation workflow…');
	}

	return new Promise<void>((resolve) => {
		if (inputString) {
			const generationRequest = showInspireProgress
				? ncApi.generateWithProgress(
						document.body.dataset.boardId!,
						task,
						inputString,
						undefined,
						(effect) => {
							if (requestId !== currentRequestId) return;
							const sourceDetail = effect.sources?.length
								? ` ${effect.sources
										.map((source) => `${source.source}: ${source.title}`)
										.join(' · ')}`
								: '';
							updateGenerateProgress(effect.stage, `${effect.detail}${sourceDetail}`);
						}
					)
				: ncApi.generate(document.body.dataset.boardId!, task, inputString);
			generationRequest
				.then((entries) => {
					if (requestId === currentRequestId) {
						const renderedEntries = showGenerateResults(task, entries, false, nodes);
						emitRenderedGenerateResults(task, renderedEntries);
					}
					resolve();
				})
				.catch(() => {
					if (requestId === currentRequestId) {
						showGenerateError();
					}
					resolve();
				});
		} else {
			hideGenerateProgress('inspire');
			resolve();
		}
	});
};

export const showImageTab = (task: GenerateTask, node: NCNode) => {
	const graph = globalThis.neuroCreate.graph;
	if (graph) {
		resetGenerate('image-inspire', fetchGenerateResults.bind(null, [node]), node);

		const container = document.querySelector('#generate-container')!;
		const loadingIndicator = container.querySelector('.loading')!;
		loadingIndicator.classList.add('hidden');
		removeChildren(document.querySelector('#generate-container .results-list')!);
		const optionsContainer = document.querySelector(
			task === 'image-inspire' ? '.image-inspire-options' : '.image-synthesise-options'
		)!;
		optionsContainer.classList.remove('hidden');

		const generateImageButton = document.createElement('image-generator-with-quota');
		generateImageButton.align = 'left';
		generateImageButton.isQuotaOnRight = true;
		optionsContainer?.append(generateImageButton);
		generateImageButton.onclick = async () => {
			loadingIndicator.classList.remove('hidden');
			await fetchGenerateResults([node], task, false);
			tutorialEvents.clickedGenerateImageResult();
		};
		tutorialEvents.clickedGenerateImage();
	}
};

export const showVideoTab = (task: GenerateTask, node: NCNode) => {
	const graph = globalThis.neuroCreate.graph;
	if (graph) {
		resetGenerate('video-inspire', fetchGenerateResults.bind(null, [node]), node);

		const container = document.querySelector('#generate-container')!;
		const loadingIndicator = container.querySelector('.loading')!;
		loadingIndicator.classList.add('hidden');
		removeChildren(document.querySelector('#generate-container .results-list')!);
		const optionsContainer = document.querySelector(
			task === 'video-inspire' ? '.video-inspire-options' : '.video-synthesise-options'
		)!;
		optionsContainer.classList.remove('hidden');

		const generateVideoButton = document.createElement('video-generator-with-quota');
		generateVideoButton.align = 'left';
		generateVideoButton.isQuotaOnRight = true;
		optionsContainer?.append(generateVideoButton);
		generateVideoButton.onclick = async () => {
			loadingIndicator.classList.remove('hidden');
			await fetchGenerateResults([node], task, false);
			tutorialEvents.clickedGenerateVideoResult();
		};
		tutorialEvents.clickedGenerateVideo();
	}
};

const stopAddingNodesToCommonIdeas = () => {
	const graph = globalThis.neuroCreate.graph;
	if (graph) {
		graph.stopSelectingNodes('common-ideas');
	}
};

const clearGenerateResults = () => {
	const container = document.querySelector('#generate-container')!;
	const loadingIndicator = container.querySelector('.loading')!;
	loadingIndicator.classList.add('hidden');

	const resultsList = container.querySelector('.results-list')!;
	removeChildren(resultsList);
};

const getInitialNodeSelection = (node: NCNode) => {
	const initialNodes = globalThis.neuroCreate.graph!.selections.commonIdeasNodes || [node];

	if (initialNodes.length > 0) {
		updateSelectedCommonIdeasNodes(initialNodes);
	}

	return initialNodes;
};

const setupSelectNodesButton = (container: HTMLDivElement) => {
	const graph = globalThis.neuroCreate.graph!;
	const getSelectedNodes = () =>
		[...container.querySelectorAll<HTMLUListElement>('ul.selected-nodes li')]
			.map((li) => graph.getNode(li.dataset.nodeId || ''))
			.filter((node): node is NCNode => !!node);

	const select = container.querySelector<HTMLButtonElement>('button.select')!;
	select.onclick = (event) => {
		event.preventDefault();
		tutorialEvents.selectingItems();

		const selectedNodes = getSelectedNodes();
		graph.startSelectingNodes('commonIdeasNodes', selectedNodes, 'common-ideas');
		showCommonIdeasNodesPrompt(selectedNodes);
	};

	document
		.querySelector('#generate-container')!
		.querySelector('.close-panel')!
		.addEventListener('click', stopAddingNodesToCommonIdeas);

	return getSelectedNodes;
};

export const showBrainstormTab = (node: NCNode) => {
	const graph = globalThis.neuroCreate.graph;
	if (graph) {
		resetGenerate('brainstorm', fetchGenerateResults.bind(null, [node]), node);

		clearGenerateResults();

		const brainstormContainer = document.querySelector<HTMLDivElement>('.brainstorm-input')!;
		brainstormContainer.classList.remove('hidden');

		const initialNodes = getInitialNodeSelection(node);

		const getSelectedNodes = setupSelectNodesButton(brainstormContainer);

		const submit = brainstormContainer.querySelector<HTMLButtonElement>('button.submit')!;
		submit.disabled = initialNodes.length < 1;
		submit.onclick = async () => {
			document.querySelector('#generate-container .loading')!.classList.remove('hidden');

			const selectedNodes = getSelectedNodes();
			await fetchGenerateResults(selectedNodes, 'brainstorm', false);
			tutorialEvents.clickedBrainstormResults(selectedNodes);
		};

		tutorialEvents.clickedBrainstorm();
	}
};

export const showCommonIdeasTab = (node: NCNode) => {
	const graph = globalThis.neuroCreate.graph;
	if (graph) {
		resetGenerate('common', fetchGenerateResults.bind(null, [node]), node);

		clearGenerateResults();

		const commonIdeasContainer = document.querySelector<HTMLDivElement>('.common-ideas-input')!;
		commonIdeasContainer.classList.remove('hidden');

		const initialNodes = getInitialNodeSelection(node);

		const getSelectedNodes = setupSelectNodesButton(commonIdeasContainer);

		const submit = commonIdeasContainer.querySelector<HTMLButtonElement>('button.submit')!;
		submit.disabled = initialNodes.length < 2;
		submit.onclick = async () => {
			document.querySelector('#generate-container .loading')!.classList.remove('hidden');

			await fetchGenerateResults(getSelectedNodes(), 'common', false);
			tutorialEvents.clickedCommonIdeasResults();
		};

		tutorialEvents.clickedCommonIdeas();
	}
};

const countSlashes = (str: string) => {
	const re = /\//g;
	return ((str || '').match(re) || []).length;
};

export const resetGenerate = (
	generateTask: GenerateTask,
	fetchGenerateResults: (generateTask: GenerateTask) => Promise<void>,
	node: NCNode
) => {
	const container = document.querySelector<HTMLElement>('#generate-container')!;
	hideGenerateProgress();
	container.querySelector('.positioning-generate-context')?.classList.add('hidden');
	container.querySelector('.positioning-generate-more')?.classList.add('hidden');
	delete container.dataset.positioningMode;
	clearFlowyHighlight();
	applyGenerateTaskButtons({ selectedTask: generateTask, positioningMode: false });
	document
		.querySelectorAll<HTMLButtonElement>('#generate-container .select-option button')
		.forEach((button) => {
			button.onclick = () => {
				if (button.dataset.task === 'common') {
					showCommonIdeasTab(node);
				} else if (button.dataset.task === 'brainstorm') {
					showBrainstormTab(node);
				} else if (button.dataset.task?.startsWith('image')) {
					showImageTab(button.dataset.task as GenerateTask, node);
				} else if (button.dataset.task?.startsWith('video')) {
					showVideoTab(button.dataset.task as GenerateTask, node);
				} else {
					fetchGenerateResults(button.dataset.task as GenerateTask);
				}
			};
		});

	const resultsList = container.querySelector('.results-list')!;
	removeChildren(resultsList);

	const loadingIndicator = container.querySelector('.loading')!;
	const waitsForExplicitSubmit = generateTask === 'common' || generateTask === 'brainstorm';

	if (generateTask !== 'common') {
		container.querySelector('.common-ideas-input')!.classList.add('hidden');
		stopAddingNodesToCommonIdeas();
	}

	if (generateTask !== 'brainstorm') {
		container.querySelector('.brainstorm-input')!.classList.add('hidden');
		stopAddingNodesToCommonIdeas();
	}

	if (waitsForExplicitSubmit) {
		loadingIndicator.classList.add('hidden');
	} else {
		loadingIndicator.classList.remove('hidden');
	}

	if (generateTask !== 'image-inspire') {
		container.querySelector('.image-inspire-options')!.classList.add('hidden');
	}

	if (generateTask === 'image-inspire' || generateTask === 'image-synthesise') {
		if (node.label) {
			const label = node.label.toLowerCase().replace(/[\W]/g, '/').replace(/\/+/g, '/');
			const requestId = uuidv4();
			currentRequestId = requestId;
			getBoardImages(document.body.dataset.boardId!).then((images) => {
				const earlierResults = images.filter((image) => {
					if (Math.abs(countSlashes(image.description) - countSlashes(label)) < 5) {
						// Number of words is similar
						return (
							image.description.includes(label) ||
							label.includes(image.description) ||
							levenshteinDistance(image.description, label) < label.length / 2
						);
					} else {
						// Label is much shorter than description
						return false;
					}
				});
				if (earlierResults.length > 0) {
					if (currentRequestId === requestId) {
						showGenerateResults(
							generateTask,
							earlierResults.map((image) => ({
								title: image.description,
								snippet: image.url,
							})),
							true,
							[node]
						);
					}
				}
			});
		}
	}

	if (generateTask !== 'video-inspire') {
		container.querySelector('.video-inspire-options')!.classList.add('hidden');
	}

	if (generateTask === 'video-inspire') {
		if (node.label) {
			const label = node.label.toLowerCase().replace(/[\W]/g, '/').replace(/\/+/g, '/');
			const requestId = uuidv4();
			currentRequestId = requestId;
			ncApi.getBoardVideos(document.body.dataset.boardId!).then((videos) => {
				const earlierResults = videos.filter((video) => {
					if (Math.abs(countSlashes(video.description) - countSlashes(label)) < 5) {
						// Number of words is similar
						return (
							video.description.includes(label) ||
							label.includes(video.description) ||
							levenshteinDistance(video.description, label) < label.length / 2
						);
					} else {
						// Label is much shorter than description
						return false;
					}
				});
				if (earlierResults.length > 0) {
					if (currentRequestId === requestId) {
						showGenerateResults(
							generateTask,
							earlierResults.map((video) => ({
								title: video.description,
								snippet: video.url,
							})),
							true,
							[node]
						);
					}
				}
			});
		}
	}

	container.querySelector('.ideate-options')!.classList.add('hidden');
	container.querySelector('.video-inspire-options video-generator-with-quota')?.remove();
	container.querySelector('.image-inspire-options image-generator-with-quota')?.remove();
};

export const createSingleNodeFromGenerate = (
	node: NCNode,
	label: string,
	src?: string,
	isVideo: boolean = false,
	generateTask?: GenerateTask
): NCNode | void => {
	const graph = globalThis.neuroCreate.graph;

	if (graph) {
		const topics = generateTask ? [`source:${generateTask}`] : [];
		return (graph as InspireGraph).addSingleNode(node, label, undefined, topics, [], src, isVideo);
	}
};

export const isGeneratePanelVisible = () => {
	return !document.querySelector('#generate-container')?.classList.contains('hidden');
};
